/**
 * API d'authentification profil Établissement.
 * Toutes les routes sous /api/auth/etablissement
 */

import { apiFetch, setStoredToken } from "./client";

const BASE = "/auth/etablissement";

// --- Types (réponses et body)

export type LoginResponse = {
  token?: string;
  accessToken?: string;
  user: EstablishmentUser;
};

export type EstablishmentUser = {
  id?: string;
  /** ID de l'établissement (pour les routes /api/etablissement/*). Si absent, utiliser id. */
  etablissementId?: string;
  email: string;
  name?: string;
  nom?: string;
  type_utilisateur?: string;
  agrement?: string;
  agre?: string;
  type?: string;
  commune?: string;
  [key: string]: unknown;
};

export type ProfileResponse = EstablishmentUser;

/** Fiche annuaire publique (recherche/parcours), jamais d'email/mot de passe/téléphone. */
export interface PublicEtablissementSummary {
  id: string;
  nom: string;
  type: string;
  ville: string | null;
  commune: string | null;
  sigle: string | null;
  statut: string;
  estRevendique: boolean;
}

export type EtablissementDirectoryParams = {
  page?: number;
  limit?: number;
  estRevendique?: boolean;
  type?: string;
  /** Recherche par nom (LIKE, insensible à la casse) — uniquement pris en compte côté serveur avec estRevendique=true. */
  q?: string;
};

export type EtablissementDirectoryResponse = {
  data: PublicEtablissementSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

/** profileData mode claim : revendiquer une fiche existante de l'annuaire. */
export type EtablissementClaimProfileData = { etablissementId: string };

/** profileData mode création : fiche inédite, absente de l'annuaire. */
export type EtablissementCreationProfileData = {
  nom: string;
  type: string;
  adresse: string;
  telephone?: string;
  numero_agrement?: string;
  directeur_nom?: string;
  directeur_prenom?: string;
};

export type SignupProfileData = EtablissementClaimProfileData | EtablissementCreationProfileData;

export type SignupBody = {
  email: string;
  password: string;
  confirmPassword: string;
  type_utilisateur: string;
  profileData: SignupProfileData;
};

export type SignupInfoResponse = {
  types?: Array<{ id?: string; code?: string; libelle?: string; label?: string }>;
  [key: string]: unknown;
};

export type ConfirmPasswordResetBody = {
  email: string;
  otp?: string;
  code?: string;
  newPassword: string;
  confirmPassword?: string;
};

// --- Appels

/** Réponse possible du backend : { data: { token, user } } ou directement { token, user } */
type LoginApiBody = LoginResponse | { success?: boolean; message?: string; data?: LoginResponse };

/** Backend user avec profile imbriqué */
type BackendUser = EstablishmentUser & { profile?: { nom?: string; type?: string; [key: string]: unknown } };

function normalizeLoginUser(user: BackendUser | undefined): EstablishmentUser | undefined {
  if (!user) return undefined;
  const profile = user.profile;
  return {
    ...user,
    nom: user.nom ?? profile?.nom ?? user.name,
    name: user.name ?? user.nom ?? profile?.nom,
    type: user.type ?? user.type_utilisateur ?? profile?.type,
  };
}

/** Aplati `profile` / `nom` pour login, verify-otp et GET `/profile` (même shape backend). */
export function normalizeAuthUser(user: EstablishmentUser | null | undefined): EstablishmentUser | null {
  if (user == null) return null;
  return normalizeLoginUser(user as BackendUser) ?? user;
}

export type LoginResult =
  | { ok: true; data: LoginResponse }
  | { ok: true; data: { requiresOtp: true; email?: string } }
  | { ok: false; message: string };

export async function login(
  email: string,
  password: string
): Promise<LoginResult> {
  const out = await apiFetch<LoginApiBody>(BASE + "/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  // Toujours exiger l'étape OTP : on n'utilise pas le token éventuellement renvoyé par login,
  // on redirige vers la page code ; le JWT sera stocké après verify-otp.
  return { ok: true, data: { requiresOtp: true, email } };
}

export type SignupResult =
  | { ok: true; data: { user: EstablishmentUser | null } }
  | { ok: false; message: string; kind: "not_found" | "already_claimed" | "generic" };

/**
 * Le signup (claim ou création) ne renvoie jamais de token exploitable : l'établissement
 * reste `en_attente` jusqu'à validation admin. Ne jamais auto-connecter ici.
 */
export async function signup(body: SignupBody): Promise<SignupResult> {
  const out = await apiFetch<{ success?: boolean; message?: string; data?: { user?: EstablishmentUser } }>(
    BASE + "/signup",
    {
      method: "POST",
      body: JSON.stringify(body),
      skipAuth: true,
    }
  );
  if (!out.ok) {
    const msg = out.message ?? "";
    const kind: "not_found" | "already_claimed" | "generic" = /introuvable dans l.annuaire/i.test(msg)
      ? "not_found"
      : /déjà un compte associé/i.test(msg)
        ? "already_claimed"
        : "generic";
    return { ok: false, message: msg, kind };
  }
  const user = out.data?.data?.user ?? null;
  return { ok: true, data: { user } };
}

export async function logout(): Promise<void> {
  // silent401 : un 401 sur /logout ne doit pas déclencher clearAuth (efface tout le persist + redirection).
  await apiFetch(BASE + "/logout", { method: "POST", silent401: true });
  setStoredToken(null);
  try {
    localStorage.removeItem("agbaza.etablissement.token");
    localStorage.removeItem("agbaza.auth.establishment");
  } catch {
    /* ignore */
  }
}

type RefreshSuccessBody = {
  data?: { token?: string; accessToken?: string };
  token?: string;
  accessToken?: string;
};

export async function refreshToken(): Promise<{ ok: true; token: string } | { ok: false }> {
  // cookie HttpOnly refreshToken : pas de body requis
  const out = await apiFetch<RefreshSuccessBody>(BASE + "/refresh-token", {
    method: "POST",
    skipAuth: true,
  });
  if (!out.ok) return { ok: false };

  const payload = out.data as unknown;

  function extractToken(node: unknown): string | null {
    let foundAccessToken: string | null = null;
    let foundToken: string | null = null;

    const visit = (n: unknown) => {
      if (typeof foundAccessToken === "string" && foundAccessToken.trim()) return;
      if (n == null) return;

      if (typeof n === "object") {
        if (Array.isArray(n)) {
          for (const item of n) visit(item);
          return;
        }

        const o = n as Record<string, unknown>;
        const a = o.accessToken;
        if (typeof a === "string" && a.trim()) {
          foundAccessToken = a.trim();
          return;
        }
        const t = o.token;
        if (typeof t === "string" && t.trim() && !foundToken) {
          foundToken = t.trim();
        }

        for (const k of Object.keys(o)) {
          visit(o[k]);
          if (foundAccessToken) return;
        }
      }
    };

    visit(node);
    return foundAccessToken ?? foundToken;
  }

  const token = extractToken(payload);
  if (typeof token === "string" && token.trim()) setStoredToken(token);
  // Contrat doc : le refresh doit renvoyer un nouvel access token dans `data.token`.
  return token && typeof token === "string" && token.trim() ? { ok: true, token } : { ok: false };
}

export async function resetPassword(
  email: string
): Promise<{ ok: true } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(BASE + "/reset-password", {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function confirmPasswordReset(
  body: ConfirmPasswordResetBody
): Promise<{ ok: true } | { ok: false; message: string }> {
  const payload = {
    email: body.email,
    otp: body.otp ?? body.code,
    code: body.code ?? body.otp,
    newPassword: body.newPassword,
    confirmPassword: body.confirmPassword ?? body.newPassword,
  };
  const out = await apiFetch<unknown>(BASE + "/confirm-password-reset", {
    method: "POST",
    body: JSON.stringify(payload),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function getProfile(options?: {
  /** Ne pas déclencher la déconnexion globale sur 401 (utilisé par initAuth au chargement). */
  silent401?: boolean;
}): Promise<
  { ok: true; data: ProfileResponse } | { ok: false; status: number; message: string }
> {
  const out = await apiFetch<ProfileResponse>(BASE + "/profile", {
    silent401: options?.silent401,
  });
  if (!out.ok) return { ok: false, status: out.status, message: out.message };
  // Contrat Backend (BaseController) : out.data = { success, message, data: { ...profil } }
  const payload = out.data as unknown as { data?: unknown } & Record<string, unknown>;
  const user = (payload?.data ?? payload) as EstablishmentUser;
  const merged = normalizeAuthUser(user);
  return { ok: true, data: merged ?? user };
}

export async function updateProfile(
  data: Partial<EstablishmentUser>
): Promise<{ ok: true; data: ProfileResponse } | { ok: false; message: string }> {
  const out = await apiFetch<ProfileResponse>(BASE + "/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!out.ok) return { ok: false, message: out.message };
  // BaseController : out.data = { success, message, data: { ...profil } }
  const payload = out.data as unknown as { data?: unknown } & Record<string, unknown>;
  const user = (payload?.data ?? payload) as ProfileResponse;
  return { ok: true, data: user };
}

export async function getSignupInfo(): Promise<
  { ok: true; data: SignupInfoResponse } | { ok: false; message: string }
> {
  const out = await apiFetch<SignupInfoResponse>(BASE + "/signup-info", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

/** Backend : soit `{ success, message, data: string[] }` (BaseController), soit un tableau nu. */
type TypesApiBody = { data?: unknown } | unknown[];

export type TypeOption = {
  id?: string;
  code?: string;
  /** Slug enum (ex. "hopital") — à privilégier comme valeur soumise au backend. */
  type?: string;
  libelle?: string;
  label?: string;
  /** Présent quand le backend renvoie les types réellement représentés dans l'annuaire. */
  count?: number;
};

/** Les pharmacies ne sont pas un type de structure proposé à l'inscription établissement. */
function isPharmacie(t: TypeOption): boolean {
  return [t.type, t.code, t.libelle, t.label].some((v) => typeof v === "string" && /pharma/i.test(v));
}

export async function getTypes(): Promise<
  { ok: true; data: TypeOption[] } | { ok: false; message: string }
> {
  const out = await apiFetch<TypesApiBody>(BASE + "/types", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message };
  const raw = Array.isArray(out.data) ? out.data : (out.data as { data?: unknown })?.data;
  const list = Array.isArray(raw) ? raw : [];
  // Le backend renvoie soit des libellés bruts (ex. "Hôpital public"), soit des objets { type, label, count }.
  const data = list
    .map((t) => (typeof t === "string" ? { code: t, libelle: t } : (t as TypeOption)))
    .filter((t) => !isPharmacie(t));
  return { ok: true, data };
}

/** Autocomplete annuaire — min 2 caractères, ne renvoie que les fiches non revendiquées. */
export async function searchEtablissements(
  q: string
): Promise<{ ok: true; data: PublicEtablissementSummary[] } | { ok: false; message: string }> {
  const query = q.trim();
  if (query.length < 2) return { ok: true, data: [] };
  const out = await apiFetch<{ data: PublicEtablissementSummary[] }>(
    BASE + "/search?q=" + encodeURIComponent(query),
    { skipAuth: true }
  );
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: Array.isArray(out.data?.data) ? out.data.data : [] };
}

/** Parcours paginé de l'annuaire complet, avec filtres optionnels. */
export async function getEtablissementDirectory(
  params: EtablissementDirectoryParams = {}
): Promise<{ ok: true; data: EtablissementDirectoryResponse } | { ok: false; message: string }> {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("limit", String(params.limit ?? 20));
  if (params.estRevendique !== undefined) sp.set("estRevendique", String(params.estRevendique));
  if (params.type) sp.set("type", params.type);
  if (params.q) sp.set("q", params.q);
  const out = await apiFetch<EtablissementDirectoryResponse>(BASE + "/directory?" + sp.toString(), {
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function validateAgrement(
  agrement: string
): Promise<{ ok: true; valid: boolean } | { ok: false; message: string }> {
  const out = await apiFetch<{ valid?: boolean }>(BASE + "/validate-agrement", {
    method: "POST",
    body: JSON.stringify({ agrement }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, valid: (out.data as { valid?: boolean }).valid ?? false };
}

/** Réponse verify-otp : peut contenir token+user (connexion) ou rien (ex. reset password) */
type VerifyOtpApiBody =
  | { success?: boolean; data?: { token?: string; accessToken?: string; user?: BackendUser } }
  | { token?: string; accessToken?: string; user?: BackendUser };

export type VerifyOtpResult =
  | { ok: true; data?: { token: string; user: EstablishmentUser } }
  | { ok: false; message: string };

/**
 * Extrait token et user quelle que soit l’imbrication ({ data }, { data: { data } }, etc.).
 * Évite les sessions « vides » si le backend renvoie un shape légèrement différent.
 */
function extractVerifyOtpTokenAndUser(body: unknown): { token?: string; rawUser?: BackendUser } {
  let token: string | undefined;
  let rawUser: BackendUser | undefined;

  const visit = (node: unknown): void => {
    if (node == null || typeof node !== "object" || Array.isArray(node)) return;
    const o = node as Record<string, unknown>;
    // Préférer accessToken quand il est présent (contrat backend).
    const t = o.accessToken ?? o.token;
    if (typeof t === "string" && t.trim() && !token) token = t.trim();
    const u = o.user;
    if (u && typeof u === "object" && !Array.isArray(u) && !rawUser) rawUser = u as BackendUser;
    if (o.data != null) visit(o.data);
  };

  visit(body);
  return { token, rawUser };
}

export async function verifyOtp(
  email: string,
  code: string
): Promise<VerifyOtpResult> {
  const out = await apiFetch<VerifyOtpApiBody>(BASE + "/verify-otp", {
    method: "POST",
    // Backend attend uniquement otpCode dans le body
    body: JSON.stringify({ otpCode: code }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  const { token, rawUser } = extractVerifyOtpTokenAndUser(out.data);
  if (token) setStoredToken(token);
  if (token && rawUser) {
    const user = normalizeLoginUser(rawUser) ?? (rawUser as EstablishmentUser);
    return { ok: true, data: { token, user } };
  }
  return { ok: true };
}
